import { ColorTypes } from '@genial/design-tokens';
import { IconographyTypes } from '@genial/icons';
import { BackendError, Service } from '@genial/services';
import {
  ManagementFeeOrderDetailsByIdSuccessResponse,
  ManagementFeeSuccessPagedListResponse,
  MetaResponse,
} from '@genial/services/dist/models';
import { Utils } from '@genial/utils';
import { startCase } from 'lodash';
import { selector, selectorFamily } from 'recoil';

import { blankMetaResponse } from '../../utils/blankMetaResponse';

import { rateManagementUploadState } from './atom';
import { ManagementFeeCustomerError, ManagementFeeStatus, screenName } from './constants';

export const rateManagementUpload = selector({
  key: 'RateManagement/Upload',
  get: ({ get }) => get(rateManagementUploadState),
});

export interface SentOrdersListResponseNormalized
  extends Utils.ArrayElement<NonNullable<ManagementFeeSuccessPagedListResponse['data']>> {
  managementFeeHashNormalized: string;
  managementFeeMonthNormalized: string;
  managementFeeSentAtNormalized: string;
  managementFeeIdNormalized: string;
  managementFeeStatusNormalized: ManagementFeeStatus;
  managementFeeStatusTextNormalized: string;
  managementFeeStatusIconNormalized: IconographyTypes.Option;
  managementFeeStatusIconColorNormalized: ColorTypes.Option;
  managementFeeStatusIconSizeNormalized: Utils.IconTypes.SizeTokens;
  managementFeeStatusIconBackgroundNormalized: ColorTypes.Option;
  managementFeeTypeNormalized: string;
  totalNormalized: string;
  totalCustomersNormalized: string;
  isProcessing: boolean;
  isInvalid: boolean;
}

export const sentOrdersQuery = selectorFamily<
  | {
      meta: MetaResponse;
      data: SentOrdersListResponseNormalized[];
    }
  | undefined,
  { page: number; searchTerm?: string }
>({
  key: 'RateManagement/SentOrders',
  get:
    ({ page, searchTerm }) =>
    async () => {
      try {
        const orders = await (searchTerm?.length
          ? Service.ManagementFee.V1.getOrdersBySearchTerm({
              screenName,
              page,
              searchTerm: searchTerm || '',
            })
          : Service.ManagementFee.V1.getAllOrders({
              screenName,
              page,
            }));

        const normalizedOrders: SentOrdersListResponseNormalized[] | null | undefined =
          orders.data?.map((order) => ({
            ...order,
            managementFeeHashNormalized: order.managementFeeHash?.toLocaleUpperCase() ?? '',
            managementFeeMonthNormalized: order.managementFeeMonth
              ? order.managementFeeMonth.toLocaleUpperCase()
              : '',
            managementFeeSentAtNormalized: order.managementFeeSentAt
              ? Utils.Mask.DateFormat(order.managementFeeSentAt, 'dd/MM/yyyy')
              : '--',
            managementFeeIdNormalized: order.managementFeeId ?? '-',
            managementFeeStatusNormalized: order.managementFeeStatus.statusId,
            managementFeeStatusTextNormalized: order.managementFeeStatus.status
              ? order.managementFeeStatus.status.toLocaleUpperCase()
              : '--',
            managementFeeStatusIconNormalized:
              (order.managementFeeStatus.statusIcon.icon as IconographyTypes.Option) ?? 'Info',
            managementFeeStatusIconColorNormalized:
              (order.managementFeeStatus.statusIcon.iconColor as ColorTypes.Option) ??
              'neutral.medium',
            managementFeeStatusIconSizeNormalized:
              (order.managementFeeStatus.statusIcon.iconSize as Utils.IconTypes.SizeTokens) ??
              Utils.Icon.Sizes.xxs,
            managementFeeStatusIconBackgroundNormalized:
              (order.managementFeeStatus.statusIcon.backgroundColor as ColorTypes.Option) ??
              'neutral.dark',
            managementFeeTypeNormalized: order.managementFeeType?.toLocaleUpperCase() ?? '-',
            totalNormalized: Utils.Mask.Money(order.total),
            totalCustomersNormalized: order.totalCustomers.toString(),
            isProcessing: order.managementFeeStatus.statusId === ManagementFeeStatus.Created,
            isInvalid: [
              ManagementFeeStatus.Cancelled,
              ManagementFeeStatus.Error,
              ManagementFeeStatus.Rejected,
              ManagementFeeStatus.Unknown,
              ManagementFeeStatus.Expired,
            ].includes(order.managementFeeStatus.statusId),
          })) ?? [];

        return {
          meta: orders.meta,
          data: normalizedOrders,
        };
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return { data: [], meta: blankMetaResponse() };
        }

        throw error;
      }
    },
});

export interface SentOrdersCardResponse {
  periodValue?: string;
  typeFee?: string;
  value: number;
  periodIntervalStart?: Date;
  periodIntervalEnd?: Date;
}

export interface SentOrdersCardResponseNormalized extends SentOrdersCardResponse {
  periodIntervalNormalized: string;
  valueNormalized: string;
  periodValueNormalized: string;
  typeFeeNormalized: string;
}

export const sentOrdersCardsQuery = selector<SentOrdersCardResponseNormalized[]>({
  key: 'RateManagement/SentOrdersCards',
  get: async () => {
    const { data } = await Service.ManagementFee.V1.getOrdersConsolidated({ screenName });
    const { consolidatedYear, consolidatedLastMonth, averageLastMonth } = data;

    const cardData: SentOrdersCardResponse[] = [
      {
        periodValue: consolidatedYear.periodAlias ? consolidatedYear.periodAlias.toUpperCase() : '',
        periodIntervalStart: consolidatedYear.startDate,
        periodIntervalEnd: consolidatedYear.finalDate,
        typeFee: 'TAXA ANUAL (R$)',
        value: consolidatedYear.total,
      },
      {
        periodValue: consolidatedLastMonth.periodAlias
          ? consolidatedLastMonth.periodAlias.toUpperCase()
          : '',
        periodIntervalStart: consolidatedLastMonth.startDate,
        periodIntervalEnd: consolidatedLastMonth.finalDate,
        typeFee: 'TAXA MENSAL (R$)',
        value: consolidatedLastMonth.total,
      },
      {
        periodValue: averageLastMonth.periodAlias ? averageLastMonth.periodAlias.toUpperCase() : '',
        periodIntervalStart: averageLastMonth.startDate,
        periodIntervalEnd: averageLastMonth.finalDate,
        typeFee: 'TICKET MÉDIO(R$)',
        value: averageLastMonth.total,
      },
    ];

    const normalizedCardData: SentOrdersCardResponseNormalized[] = cardData.map((card) => ({
      ...card,
      periodIntervalNormalized: `${Utils.Mask.DateFormat(
        card.periodIntervalStart,
        'dd/MM/yyyy'
      )} - ${Utils.Mask.DateFormat(card.periodIntervalEnd, 'dd/MM/yyyy')}`,
      valueNormalized: Utils.Mask.Money(card.value),
      periodValueNormalized: card.periodValue ?? '--',
      typeFeeNormalized: card.typeFee ?? '-',
    }));

    return normalizedCardData;
  },
});

type SentOrderDetailResponseCustomer = Utils.ArrayElement<
  NonNullable<ManagementFeeOrderDetailsByIdSuccessResponse['data']['customerFeeInfo']>
>;

type SentOrderDetailResponseCustomerNormalized = SentOrderDetailResponseCustomer & {
  customerIconNormalized?: IconographyTypes.Option;
  customerIconColorNormalized?: ColorTypes.Option;
  customerIconSizeNormalized?: Utils.IconTypes.SizeTokens;
  customerIconBackgroundNormalized?: ColorTypes.Option;
  customerNameNormalized: string;
  customerAvailableBalanceNormalized: string;
  managementFeeMonthNormalized: string;
  managementFeeRateNormalized: string;
  customerCpfCnpjNormalized: string;
  customerCustodyAccountNormalized: string;
  customerSinacorAccountNormalized: string;
  processErrorDescriptionNormalized: ManagementFeeCustomerError[] | undefined;
};

export type SentOrderDetailResponseNormalized =
  ManagementFeeOrderDetailsByIdSuccessResponse['data'] & {
    managerAccountNormalized: string;
    managerAccountDigitNormalized: string;
    managementFeeIdNormalized: string;
    managementFeeHashNormalized: string;
    managementFeeSentAtNormalized: string;
    managementFeeMonthNormalized: string;
    managementFeeStatusNormalized: ManagementFeeStatus;
    managementFeeStatusTextNormalized: string;
    managementFeeStatusIconNormalized: IconographyTypes.Option;
    managementFeeStatusIconColorNormalized: ColorTypes.Option;
    managementFeeStatusIconSizeNormalized: Utils.IconTypes.SizeTokens;
    managementFeeStatusIconBackgroundNormalized: ColorTypes.Option;
    managementFeeTypeNormalized: string;
    totalAmountToReceiveNormalized: string;
    lastUpdateNormalized: string;
    validCustomers: SentOrderDetailResponseCustomerNormalized[];
    invalidCustomers: SentOrderDetailResponseCustomerNormalized[];
    canDownloadFile: boolean;
  };

export const sentOrderDetailQuery = selectorFamily<
  SentOrderDetailResponseNormalized,
  { sentOrderId: string | number }
>({
  key: 'RateManagement/SentOrderDetail',
  get:
    ({ sentOrderId }) =>
    async () => {
      const { data: order } = await Service.ManagementFee.V1.getOrdersDetails({
        orderId: sentOrderId.toString(),
        screenName,
      });

      const normalizeCustomer = (
        customer: SentOrderDetailResponseCustomer,
        orderData: ManagementFeeOrderDetailsByIdSuccessResponse['data']
      ): SentOrderDetailResponseCustomerNormalized => ({
        ...customer,
        customerIconNormalized: customer.customerFeeStatusEntity.icon as IconographyTypes.Option,
        customerIconColorNormalized: customer.customerFeeStatusEntity
          .iconColor as ColorTypes.Option,
        customerIconSizeNormalized: customer.customerFeeStatusEntity
          .iconSize as Utils.IconTypes.SizeTokens,
        customerIconBackgroundNormalized: customer.customerFeeStatusEntity
          .backgroundColor as ColorTypes.Option,
        managementFeeMonthNormalized: orderData.managementFeeMonth
          ? orderData.managementFeeMonth.toLocaleUpperCase()
          : '--',
        managementFeeRateNormalized: Utils.Mask.Money(
          customer.movementFeeTransactionInfo.transactionValue
        ),
        customerCustodyAccountNormalized: customer.customerPnpAccountNumber
          ? `${customer.customerPnpAccountNumber}`
          : '--',
        customerSinacorAccountNormalized: `${customer.customerAccountNumber}`,
        customerNameNormalized: customer.customerName
          ? startCase(customer.customerName.toLocaleLowerCase())
          : '--',
        customerCpfCnpjNormalized: customer.customerCpfCnpjAjusted ?? '--',
        customerAvailableBalanceNormalized: Utils.Mask.Money(customer.customerAvailableBalance),
        processErrorDescriptionNormalized:
          customer.processErrorDescription?.map<ManagementFeeCustomerError>(
            (error) => error.errorId
          ),
      });

      const orderNormalized: SentOrderDetailResponseNormalized = {
        ...order,
        managerAccountNormalized: `${order.managerInfo?.managerAccountNumber}`,
        managerAccountDigitNormalized: `${order.managerInfo?.managerAccountDigit}`,
        managementFeeIdNormalized: sentOrderId.toString(),
        managementFeeHashNormalized: order.managementFeeHash?.toLocaleUpperCase() ?? '--',
        managementFeeSentAtNormalized: order.managementFeeSentAt
          ? Utils.Mask.DateFormat(order.managementFeeSentAt, 'dd/MM/yyyy')
          : '--',
        managementFeeMonthNormalized: order.managementFeeMonth
          ? order.managementFeeMonth.toLocaleUpperCase()
          : '--',
        managementFeeStatusNormalized: order.managementFeeStatus.statusId,
        managementFeeStatusTextNormalized: order.managementFeeStatus.status
          ? order.managementFeeStatus.status.toLocaleUpperCase()
          : '--',
        managementFeeStatusIconNormalized:
          (order.managementFeeStatus.statusIcon.icon as IconographyTypes.Option) ?? 'Info',
        managementFeeStatusIconColorNormalized:
          (order.managementFeeStatus.statusIcon.iconColor as ColorTypes.Option) ?? 'neutral.medium',
        managementFeeStatusIconSizeNormalized:
          (order.managementFeeStatus.statusIcon.iconSize as Utils.IconTypes.SizeTokens) ??
          Utils.Icon.Sizes.xxs,
        managementFeeStatusIconBackgroundNormalized:
          (order.managementFeeStatus.statusIcon.backgroundColor as ColorTypes.Option) ??
          'neutral.dark',
        managementFeeTypeNormalized: order.managementFeeType?.toLocaleUpperCase() ?? '--',
        totalAmountToReceiveNormalized: Utils.Mask.Money(order.totalAmountToReceive),
        lastUpdateNormalized: order.managementFeeStatus.statusUpdatedAt
          ? Utils.Mask.DateFormat(order.managementFeeStatus.statusUpdatedAt, 'dd/MM/yyyy')
          : '--',
        validCustomers:
          order.customerFeeInfo
            ?.filter((customer) => !customer.processError)
            .map((customer) => normalizeCustomer(customer, order)) ?? [],
        invalidCustomers:
          order.customerFeeInfo
            ?.filter((customer) => customer.processError)
            .map((customer) => normalizeCustomer(customer, order)) ?? [],
        canDownloadFile: order.managementFeeStatus.statusId === ManagementFeeStatus.Sent,
        lastManagerAccountNumber: order.lastManagerAccountNumber,
      };

      return orderNormalized;
    },
});
